import { useEffect, useRef } from 'react';

/**
 * Finger signature on the kiosk. Produces a PNG data URL (white strokes are never used — ink on
 * transparent). The picture stays in component state only until the form is sent.
 */
export function SignaturePad({
  onChange,
  clearLabel,
  label,
}: {
  onChange: (png: string | undefined) => void;
  clearLabel: string;
  label: string;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const inked = useRef(false);

  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    // Sharp lines on retina screens; the CSS size stays the same.
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const w = c.clientWidth || 600;
    const h = c.clientHeight || 200;
    c.width = Math.round(w * ratio);
    c.height = Math.round(h * ratio);
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#121e28';
  }, []);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  return (
    <div className="sig">
      <canvas
        ref={canvas}
        className="sig-pad"
        aria-label={label}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          drawing.current = true;
          const ctx = e.currentTarget.getContext('2d');
          const p = point(e);
          ctx?.beginPath();
          ctx?.moveTo(p.x, p.y);
          ctx?.lineTo(p.x + 0.1, p.y + 0.1);
          ctx?.stroke();
        }}
        onPointerMove={(e) => {
          if (!drawing.current) return;
          const ctx = e.currentTarget.getContext('2d');
          const p = point(e);
          ctx?.lineTo(p.x, p.y);
          ctx?.stroke();
          inked.current = true;
        }}
        onPointerUp={(e) => {
          drawing.current = false;
          if (inked.current) onChange(e.currentTarget.toDataURL('image/png'));
        }}
        onPointerCancel={() => {
          drawing.current = false;
        }}
      />
      <div className="sig-foot">
        <span>{label}</span>
        <button
          type="button"
          className="btn ghost"
          onClick={() => {
            const c = canvas.current;
            c?.getContext('2d')?.clearRect(0, 0, c.width, c.height);
            inked.current = false;
            onChange(undefined);
          }}
        >
          {clearLabel}
        </button>
      </div>
    </div>
  );
}
